import { Component, OnInit, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { GraphUser } from '../models/graph-user.model';
import { DataService } from '../services/data.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import _ from "lodash";
import { EventEmitter } from '@angular/core';

@Component({
  selector: 'app-approver-dialog',
  templateUrl: './approver-dialog.component.html',
  styleUrls: ['./approver-dialog.component.scss']
})
export class ApproverDialogComponent implements OnInit {
  approverList: any[];
  additionalApprovers: GraphUser[];
  additionalApproverControl: FormControl[];
  additionalApproverOptions: GraphUser[][];
  doaAmounts: number[];
  // Tracks, per doaAmounts index, whether the last DOA lookup failed (so a stale/cached 0 isn't mistaken for a real amount).
  doaLookupFailed: boolean[] = [];
  // Tracks whether a lookup (including its automatic retries) is still in flight, so the UI doesn't
  // prematurely clear the failed state and flash a stale amount before the retries finish.
  doaLookupLoading: boolean[] = [];
  private readonly DOA_FAILURE_LOG_KEY = "doaFailureLog";
  xmlDoc: any;
  parser: DOMParser;

  onYes = new EventEmitter();

  constructor(private dataService: DataService, private dialog: MatDialog, @Inject(MAT_DIALOG_DATA) public data: any, 
    public dialogRef: MatDialogRef<ApproverDialogComponent>) {
    this.parser = new DOMParser();
   }

  ngOnInit() {
    this.approverList = JSON.parse(JSON.stringify(this.data.approverList));
    this.additionalApprovers = JSON.parse(JSON.stringify(this.data.additionalApprovers));
    this.doaAmounts = JSON.parse(JSON.stringify(this.data.doaAmounts));
    this.additionalApproverControl = [];
    this.additionalApproverOptions = [];
    for (let i = 0; i < this.approverList.length; i++) {
      this.additionalApproverControl.push(new FormControl());
      this.additionalApproverOptions.push([]);
    }
    this.doaLookupFailed = this.doaAmounts.map(() => false);
    this.doaLookupLoading = this.doaAmounts.map(() => false);
    if (this.data.addNewRow) {
      this.addNewAdditionalApprover();
    }
  }

  addNewAdditionalApprover() {
    this.additionalApproverControl.push(new FormControl());
    this.additionalApproverOptions.push([]);
    this.approverList.push('');
    this.doaAmounts.push(0);
    this.doaLookupFailed.push(false);
    this.doaLookupLoading.push(false);
    this.additionalApprovers.push(null);
  }

  deleteApprover(index: number) {
    this.additionalApproverControl.splice(index, 1);
    this.additionalApproverOptions.splice(index, 1);
    this.additionalApprovers.splice(index, 1);
    this.approverList.splice(index, 1);
    this.doaAmounts.splice(index + 1, 1);
    this.doaLookupFailed.splice(index + 1, 1);
    this.doaLookupLoading.splice(index + 1, 1);
  }

  retryDoaLookup(index: number) {
    const approver = this.approverList[index - 1];
    if (!approver || !approver.mailNickname) {
      return;
    }
    this.getDOA(approver.mailNickname, index);
  }

  // Records failed DOA lookups (console + sessionStorage) so recurring failures can be diagnosed later.
  private logDoaFailure(lanID: string, index: number, reason: string, detail: any): void {
    const entry = {
      lanID,
      index,
      reason,
      detail: detail instanceof Error ? detail.message : detail,
      timestamp: new Date().toISOString(),
    };
    console.error("[DOA lookup failed]", entry);
    try {
      const log = JSON.parse(sessionStorage.getItem(this.DOA_FAILURE_LOG_KEY) || "[]");
      log.push(entry);
      // Keep the log bounded so sessionStorage doesn't grow unbounded during a long session.
      sessionStorage.setItem(this.DOA_FAILURE_LOG_KEY, JSON.stringify(log.slice(-25)));
    } catch {
      // best-effort logging only
    }
  }

  private handleDoaFailure(lanID: string, index: number, reason: string, detail: any): void {
    this.doaLookupFailed[index] = true;
    this.doaLookupLoading[index] = false;
    this.doaAmounts[index] = 0;
    this.logDoaFailure(lanID, index, reason, detail);
  }

  additionalApproverSelected(event: any, index: number) {
    this.getDOA(event.mailNickname, index + 1);
    this.approverList[index] = event;
  }

  getDOA(lanID: string, index: number) {
    this.doaLookupLoading[index] = true;
    let doc = document.implementation.createDocument("", "", null);
    const pi = doc.createProcessingInstruction('xml', 'version="1.0" encoding="UTF-8"');
    doc.insertBefore(pi, doc.firstChild);
    let elem1 = doc.createElement("soapenv:Envelope");
    elem1.setAttribute("xmlns:soapenv", "http://schemas.xmlsoap.org/soap/envelope/");
    elem1.setAttribute("xmlns:web", "http://duke-energy.com/webservices/");
    let elem2 = doc.createElement("soapenv:Header");
    let elem3 = doc.createElement("soapenv:Body");
    let elem4 = doc.createElement("web:GetDoaInformationByLanid");
    let elem5 = doc.createElement("web:lanids");
    let elem6 = doc.createElement("web:string");
    elem6.innerHTML = lanID;
    elem5.appendChild(elem6);
    elem4.appendChild(elem5);
    elem3.appendChild(elem4);
    elem1.appendChild(elem2);
    elem1.appendChild(elem3);
    doc.appendChild(elem1);
    let finalString = doc.documentElement.outerHTML;
    this.dataService.getDOA(finalString).subscribe((result) => {
      this.xmlDoc = this.parser.parseFromString(result, "text/xml");
      if (this.xmlDoc.getElementsByTagName("StatusCode")[0].innerHTML === 'ERROR') {
        /*let dataStructure;
        let error = this.xmlDoc.getElementsByTagName("StatusCodeMsg")[0].innerHTML;
        dataStructure = {
          error: true,
          data: error,
          isDOA: true
        }
        this.dialog.open(SuccessDialogComponent, {
          disableClose: true,
          data: dataStructure
        });*/
        const statusMsg = this.xmlDoc.getElementsByTagName("StatusCodeMsg")[0].innerHTML;
        this.handleDoaFailure(lanID, index, "SOAP_ERROR", statusMsg);
        let dataStructure = {
          errorType: 'Error Retrieving DOA Amount',
          errorDescription: 'An error occured when retrieving DOA information. Please click Retry to try again'
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true
        });
      }
      else {
        this.doaAmounts[index] = Number(this.xmlDoc.getElementsByTagName("Amount")[0].innerHTML);
        this.doaLookupFailed[index] = false;
        this.doaLookupLoading[index] = false;
      }
    }, error => {
      this.handleDoaFailure(lanID, index, "HTTP_ERROR", error);
      let dataStructure = {
        errorType: 'Error Retrieving DOA Amount',
        errorDescription: 'An error occured when retrieving DOA information. Please click Retry to try again'
      };
      this.dialog.open(ErrorDialogComponent, {
        data: dataStructure,
        disableClose: true
      });
    });
  }

  deleteAdditionalApproverValue(i: number) {
    this.additionalApprovers[i] = '' as any;
  }

  autocompleteName(index: number) {
    return 'additionalApprover' + index.toString();
  }

  displayFnAdditionalApprover(value: GraphUser): any | undefined {
    if (!value) {
      return '';
    }
    else if (value.givenName && value.surname && value.jobTitle) {
      return value.givenName + ' ' + value.surname + ' (' + value.mail + '), ' + value.jobTitle;
    }
    else if (value.givenName && value.surname && !value.jobTitle) {
      return value.givenName + ' ' + value.surname + ' (' + value.mail + ')';
    }
    else if (value.displayName && value.jobTitle) {
      return value.displayName + ' (' + value.mail + '), ' + value.jobTitle;
    }
    else if (value.displayName && !value.jobTitle) {
      return value.displayName + ' (' + value.mail + ')';
    }
    else {
      return value;
    }
  }

  searchUsersAdditionalApprover(searchText: any, index: number) {
    this.dataService.searchUsers(searchText, { excludeContractors: true }).subscribe((result) => {
      this.additionalApproverOptions[index] = _.orderBy(result['value'], ['surname', 'givenName']);
      document.getElementsByName(this.autocompleteName(index))[0].focus();
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveAndClose() {
    let dataStructure = {
      approverList: this.approverList,
      doaAmounts: this.doaAmounts
    }
    this.onYes.emit(dataStructure);
    this.dialogRef.close();
  }
}
